import stringHash from "string-hash";

import type {GqlOperation} from "@khanacademy/wonder-blocks-data";
import {moduleEndpoints} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";
import {DeployInfo, InitialRequestUrl, RequestInfo} from "@khan/shared-core";

import {Persist, type GqlContextRecord} from "./types.ts";

/**
 * Get the URI for a given GraphQL request.
 *
 * This determines what URI will be used to make a request for the given
 * GraphQL request.
 */
export const getGqlUri = <TData, TVariables extends Record<any, any>>(
    operation: GqlOperation<TData, TVariables>,
    {module, curriculum, fastlyCaching, targetLocale}: GqlContextRecord,
    variables?: TVariables | null,
): URL => {
    // Determine what endpoint we'll be contacting.
    // We can be safe that the context has a proper GAEModule here and
    // we have error checking to assert it also.
    const endpoint = moduleEndpoints[module];
    if (endpoint == null) {
        throw new KAError("No endpoint for module", Errors.Internal, {
            sentryData: {tags: {module}},
        });
    }

    // Build a basic URI for our endpoint.
    // Then we're going to augment it with the additional info.
    const uri = new URL(endpoint.path, InitialRequestUrl.origin);

    // If the endpoint path has our __opname__ placeholder, then we want to
    // insert the operation name there, otherwise we add it as a query param.
    const substituteOpNameInPath = uri.pathname.indexOf("__opname__") > -1;
    if (substituteOpNameInPath) {
        uri.pathname = uri.pathname.replace(/__opname__/g, operation.id);
    } else {
        uri.searchParams.set("opname", operation.id);
    }

    // If our endpoint supports Fastly caching and we have a fastly caching
    // rule in our context, let's add that.
    if (
        endpoint.supportsFastlyCaching &&
        fastlyCaching &&
        // NOTE #1: We disable Fastly caching in the dev environment.  This improves
        // performance when making changes to queries using Fastly caching since
        // we don't have to wait for the safelist to be recompute.  It also solves
        // an issue when using "Prod Backend" services where we can't recompute
        // the safelist.
        // sync-start:dev-cached-query 1524233751 ./libs/data/gql-apollo/src/internal/create-client.ts
        !RequestInfo.IS_DEV_SERVER
        // sync-end:dev-cached-query
    ) {
        uri.searchParams.set("fastly_cacheable", fastlyCaching);

        // sync-start:gql-pcv-param 1424454533 ./apps/khanacademy/src/testing/gql/internal/mock-link.ts
        // sync-start:gql-pcv-param 1191083968 ./apps/khanacademy/src/testing/gql/mock-gql-fetch.ts
        // sync-start:gql-pcv-param 719748383 ./libs/data/gql-apollo/src/internal/create-client.ts
        if (fastlyCaching === (Persist.UntilPublish as string)) {
            // If we're persisting until publish, we include the published
            // content version (pcv) as a query param to ensure we cache bust
            // on publish. This helps ensure that SSR retrieves the correct
            // information, rather than a stale cached value.
            uri.searchParams.set("pcv", DeployInfo.commitSHA);
        }
        // sync-end:gql-pcv-param

        // If the endpoint also supports GET methods, then we need to
        // add the query hash and variables.
        // NOTE(somewhatabstract): If we start using GET requests outside of
        // Fastly cached GraphQL, we would need to to do this next bit
        // regardless of the fastly bit above.
        if (endpoint.supportsGet) {
            // We need the string defining the GraphQL operation as defined
            // within graphql-tag (i.e. the query string).
            const {query} = operation;
            // @ts-expect-error - TS2345 - Argument of type 'number' is not assignable to parameter of type 'string'. | TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string'.
            uri.searchParams.set("hash", stringHash(query));

            // If we have variables, then add them.
            if (variables != null && Object.keys(variables).length > 0) {
                uri.searchParams.set("variables", JSON.stringify(variables));
            }
        }
    }

    // We allow devs to set a schema mode by adding ?schemaMode=<mode>
    // to the url of a page. This allows them to test how a
    // page might look if a different service manages a field.
    if (InitialRequestUrl.configParams.schemaMode != null) {
        uri.searchParams.set(
            "schemaMode",
            InitialRequestUrl.configParams.schemaMode as string,
        );
    }

    // The context should always specify the targetLocale.
    uri.searchParams.set("lang", targetLocale);

    // The context may provide a curriculum, but it is optional so that we
    // can get better cache hit rates for operations that don't need it.
    if (curriculum) {
        uri.searchParams.set("curriculum", curriculum);
    }

    // And now we have set things up, we can return the URI.
    return uri;
};
